import type { DamageElement } from "./types";

export type SkillImpactKind = "strike" | "magic" | "support";
const activeImpacts = new Set<() => void>();
const MAX_ACTIVE_IMPACTS = 4;
const SIZE = 256;
const COLORS: Record<string, string> = {
  fire: "#ed9167",
  water: "#85c9df",
  earth: "#ddc18a",
  wood: "#96c58d",
  neutral: "#c9b2ea",
};

export function playSkillImpact(
  target: HTMLElement,
  kind: SkillImpactKind,
  element: DamageElement = "neutral",
): () => void {
  const media = window.matchMedia("(prefers-reduced-motion: reduce)");
  if (media.matches || document.hidden || !target.isConnected) return () => undefined;
  const root = document.documentElement;
  const strength = (channel: string) =>
    root.dataset[`effect${channel[0].toUpperCase()}${channel.slice(1)}`] === "off"
      ? 0
      : Math.max(0, Math.min(1, Number(root.style.getPropertyValue(`--effect-${channel}`)) || 0));
  if (Math.max(strength("impact"), strength("particles"), strength("lighting")) === 0)
    return () => undefined;
  if (activeImpacts.size >= MAX_ACTIVE_IMPACTS) activeImpacts.values().next().value?.();
  const canvas = document.createElement("canvas");
  canvas.className = "skill-impact-canvas";
  canvas.setAttribute("aria-hidden", "true");
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => undefined;
  ctx.imageSmoothingEnabled = false;
  document.body.append(canvas);
  const color =
    kind === "support"
      ? "#9dd8b3"
      : kind === "strike"
        ? "#edd5a1"
        : (COLORS[element] ?? COLORS.neutral);
  const particles = Array.from({ length: Math.round(44 * strength("particles")) }, (_, index) => {
    const angle = index * 2.39996;
    const speed = 42 + (index % 7) * 12;
    return {
      vx: Math.cos(angle) * speed,
      vy: kind === "support" ? -40 - (index % 6) * 15 : Math.sin(angle) * speed - 32,
      size: index % 4 === 0 ? 5 : 3,
      life: 370 + (index % 5) * 50,
    };
  });
  let frame = 0;
  let cancelled = false;
  const finish = () => {
    if (cancelled) return;
    cancelled = true;
    cancelAnimationFrame(frame);
    canvas.remove();
    activeImpacts.delete(finish);
  };
  activeImpacts.add(finish);
  const start = performance.now();
  const draw = (now: number) => {
    if (cancelled) return;
    const elapsed = now - start;
    if (elapsed > 650 || media.matches || document.hidden || !target.isConnected) {
      finish();
      return;
    }
    const rect = target.getBoundingClientRect();
    canvas.style.left = `${Math.round(rect.left + rect.width / 2 - SIZE / 2)}px`;
    canvas.style.top = `${Math.round(rect.top + rect.height / 2 - SIZE / 2)}px`;
    ctx.clearRect(0, 0, SIZE, SIZE);
    const impact = strength("impact");
    const lighting = strength("lighting");
    const particleStrength = strength("particles");
    // Staggered stepped slashes make a fast hit followed by a shorter secondary cut.
    for (let cut = 0; cut < (kind === "strike" ? 2 : 1); cut++) {
      const phase = (elapsed - cut * 65) / 180;
      if (phase < 0 || phase >= 1) continue;
      ctx.globalAlpha = (1 - phase) * impact;
      ctx.fillStyle = color;
      for (let step = -7; step <= 7; step++) {
        const x = 128 + step * 5;
        const y = 128 + step * (cut === 0 ? -4 : 4);
        if (kind === "support") ctx.fillRect(126, 98 + step * 3, 4, 5);
        else ctx.fillRect(x, y, kind === "strike" ? 12 : 7, 4);
      }
    }
    if (elapsed < 110 && lighting > 0) {
      ctx.globalAlpha = (1 - elapsed / 110) * lighting * 0.8;
      ctx.fillStyle = "#fff5df";
      ctx.fillRect(119, 123, 18, 10);
      ctx.fillRect(123, 115, 10, 26);
    }
    particles.forEach((particle, index) => {
      const time = elapsed / 1000;
      const life = Math.min(1, elapsed / particle.life);
      ctx.globalAlpha = (1 - life) * particleStrength;
      ctx.fillStyle = index % 4 === 0 ? "#fff1d1" : color;
      const x = Math.round(128 + particle.vx * time);
      const y = Math.round(128 + particle.vy * time + (kind === "support" ? 0 : 150 * time * time));
      ctx.fillRect(x, y, particle.size, index % 3 === 0 ? 2 : particle.size);
      if (elapsed < 160 && index % 3 === 0) {
        ctx.globalAlpha *= 0.4;
        ctx.fillRect(
          Math.round(x - particle.vx * 0.025),
          Math.round(y - particle.vy * 0.025),
          3,
          2,
        );
      }
    });
    frame = requestAnimationFrame(draw);
  };
  frame = requestAnimationFrame(draw);
  return finish;
}
